import React, { useEffect, useState } from "react";
import {
  Ban, BellRing, Check, CheckCheck, Clock, FileDown, Landmark, MoreHorizontal,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAppDispatch } from "../../app/hooks";
import {
  confirmPayment,
  downloadPaymentDocument,
  markPaymentOverdue,
  markPaymentReceived,
  sendPaymentReminder,
} from "../../features/payments/slice-extensions/payments-slice-extension";
import { collectPayment } from "../../features/collection/slice-extensions/collection-slice-extension";
import { DebitAttemptStatus } from "../../features/collection/models/DebitAttemptStatus";
import { Payment } from "../../features/payments/models/Payment";
import { PaymentStatus } from "../../features/payments/models/PaymentStatus";

interface PaymentRowActionsProps {
  payment: Payment;
  collectionReady: boolean;
  onVoid: () => void;
  onChanged: () => void;
  onError: (message: string | null) => void;
}

const itemClass =
  "flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-left text-sm text-grey-10 hover:bg-grey-95 dark:text-grey-90 dark:hover:bg-primary-20";

/**
 * A payment row's actions: the one obvious next step for its status stays
 * visible (Confirm a draft, mark owed money Received); everything else —
 * overdue, collection, reminder, document, void — collapses into a kebab
 * menu so rows stay scannable. Mirrors ContractRowActions.
 */
const PaymentRowActions: React.FC<PaymentRowActionsProps> = ({
  payment: p, collectionReady, onVoid, onChanged, onError,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reminded, setReminded] = useState(false);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!(e.target as Element).closest("[data-payment-menu]")) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const owed = p.status === PaymentStatus.Invoiced || p.status === PaymentStatus.Overdue;
  const canCollect = collectionReady && owed && p.latestDebitStatus !== DebitAttemptStatus.Processing;
  const hasDocument = owed || p.status === PaymentStatus.Received;

  const pick = (action: () => void) => () => {
    setOpen(false);
    action();
  };

  const handleCollect = async () => {
    onError(null);
    try {
      await dispatch(collectPayment(p.id)).unwrap();
      onChanged();
    } catch (error) {
      onError((error as { message?: string })?.message ?? t("collection.debit.collectFailed"));
    }
  };

  const handleDownload = async () => {
    if (busy) return;
    setBusy(true);
    const result = await dispatch(downloadPaymentDocument({ id: p.id }));
    setBusy(false);
    if (downloadPaymentDocument.rejected.match(result)) {
      onError(result.payload?.message ?? t("billing.documents.downloadFailed"));
    }
  };

  const handleRemind = async () => {
    if (busy || reminded) return;
    setBusy(true);
    const result = await dispatch(sendPaymentReminder(p.id));
    setBusy(false);
    if (sendPaymentReminder.rejected.match(result)) {
      toast.error(result.payload?.message ?? t("billing.reminder.failed", "The reminder could not be sent."));
      return;
    }
    setReminded(true);
    toast.success(t("billing.reminder.sent", "Reminder sent"));
  };

  const hasMenu = p.status === PaymentStatus.Invoiced || canCollect
    || p.status === PaymentStatus.Overdue || hasDocument || p.status !== PaymentStatus.Voided;

  return (
    <div className="relative inline-flex items-center gap-1" data-payment-menu>
      {p.status === PaymentStatus.Draft && (
        <button
          type="button"
          onClick={async () => { await dispatch(confirmPayment({ id: p.id, request: {} })); onChanged(); }}
          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-primary-40 hover:bg-grey-90 dark:text-primary-90 dark:hover:bg-primary-20"
        >
          <Check size={14} />
          <span>{t("billing.actions.confirm")}</span>
        </button>
      )}
      {owed && (
        <button
          type="button"
          onClick={async () => { await dispatch(markPaymentReceived({ id: p.id, request: {} })); onChanged(); }}
          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-primary-40 hover:bg-grey-90 dark:text-primary-90 dark:hover:bg-primary-20"
        >
          <CheckCheck size={14} />
          <span>{t("billing.actions.received")}</span>
        </button>
      )}
      {hasMenu && (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="rounded-md p-1 text-grey-30 hover:bg-grey-90 dark:text-grey-70 dark:hover:bg-primary-20"
          title={t("billing.payments.rowMenuTitle", "Payment actions")}
        >
          <MoreHorizontal size={16} />
        </button>
      )}

      {open && (
        <div className="absolute right-0 top-8 z-20 w-44 rounded-xl border border-grey-90 bg-white p-1 shadow-lg dark:border-primary-20 dark:bg-primary-10">
          {p.status === PaymentStatus.Invoiced && (
            <button type="button" className={itemClass}
              onClick={pick(async () => { await dispatch(markPaymentOverdue({ id: p.id, request: {} })); onChanged(); })}>
              <Clock size={14} />
              <span>{t("billing.actions.overdue")}</span>
            </button>
          )}
          {canCollect && (
            <button type="button" className={itemClass} onClick={pick(handleCollect)}>
              <Landmark size={14} />
              <span>
                {p.latestDebitStatus === DebitAttemptStatus.Failed ||
                p.latestDebitStatus === DebitAttemptStatus.NeedsMember
                  ? t("collection.actions.collectAgain")
                  : t("collection.actions.collect")}
              </span>
            </button>
          )}
          {p.status === PaymentStatus.Overdue && (
            <button type="button" className={itemClass} onClick={pick(handleRemind)} disabled={busy || reminded}>
              <BellRing size={14} />
              <span>
                {reminded
                  ? t("billing.reminder.sentShort", "Reminded")
                  : t("billing.reminder.send", "Send reminder")}
              </span>
            </button>
          )}
          {hasDocument && (
            <button type="button" className={itemClass} onClick={pick(handleDownload)} disabled={busy}>
              <FileDown size={14} />
              <span>
                {owed ? t("billing.documents.invoice") : t("billing.documents.receipt")}
              </span>
            </button>
          )}
          {p.status !== PaymentStatus.Voided && (
            <button
              type="button"
              className="flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-left text-sm text-red-600 hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-900/30"
              onClick={pick(onVoid)}
            >
              <Ban size={14} />
              <span>{t("billing.actions.void")}</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default PaymentRowActions;
